import { useEffect, useCallback } from 'react';

import { useSelector } from 'react-redux';

import {
  DiffViewMode,
  getGlobalDiffViewMode,
  getGlobalIsColorBlindModeEnabled,
  getGlobalIsWordDiffEnabled,
  getGlobalShouldIgnoreWhitespace,
  getGlobalIsAnnotationsEnabled,
  getGlobalIsSingleFileModeEnabled,
  FileViewMode,
  getGlobalFileViewMode,
  getGlobalTabSize,
} from 'src/redux/diff-settings';
import {
  getPullRequestDestinationRepoUuid,
  getPullRequestSourceRepoUuid,
  getCurrentPullRequestId,
  getPullRequestDiffLinesCount,
  pullRequestRenderingLimits,
} from 'src/redux/pull-request/selectors';
import { getCodeReviewSidebarWidth } from 'src/redux/sidebar';
import {
  PullRequestAttribute,
  UpdateSettings,
} from 'src/sections/repository/types';
import { getFileTreeHrefs } from 'src/selectors/file-tree-selectors';
import { getIsMobileHeaderActive } from 'src/selectors/global-selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { DiffStatStatus } from 'src/types/diffstat';
import { BucketState } from 'src/types/state';
import {
  publishFullScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';

type PullRequestScreenEventOptions = {
  name: string;
  attributes?: {};
  skip?: boolean;
};

export enum actionSubjectIds {
  fileTreeCard = 'fileTreeCard',
  fileTreeCardExpander = 'fileTreeCardExpander',
  fileTreeFile = 'fileTreeFile',
  fileTreeFolder = 'fileTreeFolder',
  fileListFile = 'fileListFile',
  fileListFolder = 'fileListFolder',
  saveButton = 'saveButton',
  settingsButton = 'settingsButton',
  detailsTab = 'detailsTab',
  filesTab = 'filesTab',
  activitiesTab = 'activitiesTab',
  sidebarExpander = 'sidebarExpander',
}
enum actionSubjects {
  button = 'button',
  card = 'card',
  tab = 'tab',
  sidebar = 'sidebar',
}
enum actions {
  clicked = 'clicked',
}
enum source {
  pullRequest = 'pullRequest',
  repository = 'repository',
}

export const usePullRequestScreenEvent = ({
  name,
  attributes = {},
  skip = false,
}: PullRequestScreenEventOptions) => {
  const toRepositoryUuid = useSelector<BucketState, string | null | undefined>(
    getPullRequestDestinationRepoUuid
  );

  const fromRepositoryUuid = useSelector<
    BucketState,
    string | null | undefined
  >(getPullRequestSourceRepoUuid);

  const pullRequestId = useSelector<BucketState, number | null>(
    getCurrentPullRequestId
  );

  useEffect(() => {
    if (toRepositoryUuid && pullRequestId && fromRepositoryUuid && !skip) {
      publishFullScreenEvent({
        name,
        containerType: 'repository',
        containerId: toRepositoryUuid,
        objectType: 'pullRequest',
        objectId: pullRequestId.toString(),
        attributes: { fromRepositoryUuid, ...attributes },
      });
    }
  }, [
    pullRequestId,
    fromRepositoryUuid,
    toRepositoryUuid,
    attributes,
    name,
    skip,
  ]);
};

export const usePullRequestAttributes = (): PullRequestAttribute => {
  const repoUUID = useSelector<BucketState, string | null | undefined>(
    getCurrentRepositoryUuid
  );
  const prID = useSelector<BucketState, number | null>(getCurrentPullRequestId);
  const fileHrefs: string[] = useSelector<BucketState, string[]>(
    getFileTreeHrefs
  );
  const sidebarWidth = useSelector<BucketState, number>(
    getCodeReviewSidebarWidth
  );
  const isMobile = useSelector<BucketState, boolean>(getIsMobileHeaderActive);
  const globalDiffViewMode = useSelector<BucketState, DiffViewMode>(
    getGlobalDiffViewMode
  );
  const globalFileViewMode = useSelector<BucketState, FileViewMode>(
    getGlobalFileViewMode
  );
  const globalTabSize = useSelector<BucketState, number>(getGlobalTabSize);
  const globalIsWordDiffEnabled = useSelector<BucketState, boolean>(
    getGlobalIsWordDiffEnabled
  );
  const globalShouldIgnoreWhitespace = useSelector<BucketState, boolean>(
    getGlobalShouldIgnoreWhitespace
  );
  const globalIsColorBlindModeEnabled = useSelector<BucketState, boolean>(
    getGlobalIsColorBlindModeEnabled
  );
  const globalIsAnnotationsEnabled = useSelector<BucketState, boolean>(
    getGlobalIsAnnotationsEnabled
  );
  const globalIsSingleFileModeEnabled = useSelector<BucketState, boolean>(
    getGlobalIsSingleFileModeEnabled
  );
  const pullRequestDiffLinesCount = useSelector<BucketState, number>(
    getPullRequestDiffLinesCount
  );
  const diffSettings: UpdateSettings = {
    diffViewMode: globalDiffViewMode,
    fileViewMode: globalFileViewMode,
    tabSize: globalTabSize,
    isWordDiffEnabled: globalIsWordDiffEnabled,
    shouldIgnoreWhitespace: globalShouldIgnoreWhitespace,
    isColorBlindModeEnabled: globalIsColorBlindModeEnabled,
    isAnnotationsEnabled: globalIsAnnotationsEnabled,
    isSingleFileModeEnabled: globalIsSingleFileModeEnabled,
  };
  return {
    repoUUID,
    prID,
    fileHrefs,
    pullRequestDiffLinesCount,
    sidebarWidth,
    isMobile,
    diffSettings,
    renderingLimits: pullRequestRenderingLimits,
  };
};

export const getPullRequestAttributes = (
  state: BucketState
): PullRequestAttribute => {
  const repoUUID = getCurrentRepositoryUuid(state);
  const prID = getCurrentPullRequestId(state);
  const fileHrefs: string[] = getFileTreeHrefs(state);
  const sidebarWidth = getCodeReviewSidebarWidth(state);
  const isMobile = getIsMobileHeaderActive(state);
  const globalDiffViewMode = getGlobalDiffViewMode(state);
  const globalFileViewMode = getGlobalFileViewMode(state);
  const globalTabSize = getGlobalTabSize(state);
  const globalIsWordDiffEnabled = getGlobalIsWordDiffEnabled(state);
  const globalShouldIgnoreWhitespace = getGlobalShouldIgnoreWhitespace(state);
  const globalIsColorBlindModeEnabled = getGlobalIsColorBlindModeEnabled(state);
  const globalIsAnnotationsEnabled = getGlobalIsAnnotationsEnabled(state);
  const globalIsSingleFileModeEnabled = getGlobalIsSingleFileModeEnabled(state);
  const pullRequestDiffLinesCount = getPullRequestDiffLinesCount(state);
  const diffSettings: UpdateSettings = {
    diffViewMode: globalDiffViewMode,
    fileViewMode: globalFileViewMode,
    tabSize: globalTabSize,
    isWordDiffEnabled: globalIsWordDiffEnabled,
    shouldIgnoreWhitespace: globalShouldIgnoreWhitespace,
    isColorBlindModeEnabled: globalIsColorBlindModeEnabled,
    isAnnotationsEnabled: globalIsAnnotationsEnabled,
    isSingleFileModeEnabled: globalIsSingleFileModeEnabled,
  };
  return {
    repoUUID,
    prID,
    fileHrefs,
    pullRequestDiffLinesCount,
    sidebarWidth,
    isMobile,
    diffSettings,
    renderingLimits: pullRequestRenderingLimits,
  };
};

export const useDiffSettingsSaveButtonAnalytics = () => {
  const attributes = usePullRequestAttributes();
  return useCallback(
    (newSettings: UpdateSettings) => {
      const settingsDifference = Object.keys(attributes.diffSettings).reduce(
        (difference: any, key: string) => {
          if (attributes.diffSettings[key] !== newSettings![key]) {
            difference[key] = newSettings![key];
          }
          return difference;
        },
        {}
      );
      publishUiEvent({
        action: actions.clicked,
        actionSubject: actionSubjects.button,
        actionSubjectId: actionSubjectIds.saveButton,
        source: source.pullRequest,
        objectId: `${attributes.repoUUID}/${attributes.prID}`,
        objectType: source.repository,
        attributes: {
          isMobile: attributes.isMobile,
          expandedWidth: attributes.sidebarWidth,
          diffLineCount: attributes.pullRequestDiffLinesCount,
          numOfFiles: attributes.fileHrefs.length,
          changedSettings: settingsDifference,
        },
      });
    },
    [attributes]
  );
};

export const useDiffSettingsButtonAnalytics = () => {
  const attributes = usePullRequestAttributes();
  return useCallback(() => {
    publishUiEvent({
      action: actions.clicked,
      actionSubject: actionSubjects.button,
      actionSubjectId: actionSubjectIds.settingsButton,
      source: source.pullRequest,
      objectId: `${attributes.repoUUID}/${attributes.prID}`,
      objectType: source.repository,
      attributes: {
        isMobile: attributes.isMobile,
        expandedWidth: attributes.sidebarWidth,
        diffLineCount: attributes.pullRequestDiffLinesCount,
        numOfFiles: attributes.fileHrefs.length,
        currentSettings: attributes.diffSettings,
      },
    });
  }, [attributes]);
};

export const usePrFileViewAnalytics = () => {
  const attributes = usePullRequestAttributes();
  return useCallback(
    (id: string, isCardCollapsed?: boolean) => {
      publishUiEvent({
        action: actions.clicked,
        actionSubject: actionSubjects.card,
        actionSubjectId: id,
        source: source.pullRequest,
        objectId: `${attributes.repoUUID}/${attributes.prID}`,
        objectType: source.repository,
        attributes: {
          isMobile: attributes.isMobile,
          expandedWidth: attributes.sidebarWidth,
          diffLineCount: attributes.pullRequestDiffLinesCount,
          numOfFiles: attributes.fileHrefs.length,
          currentSettings: attributes.diffSettings,
          isCardCollapsed,
        },
      });
    },
    [attributes]
  );
};

export const usePrFileViewFileAnalytics = () => {
  const attributes = usePullRequestAttributes();
  return useCallback(
    (
      fileName: string,
      isConflicted: boolean | null | undefined,
      fileDiffStatus: DiffStatStatus | null | undefined
    ) => {
      publishUiEvent({
        action: actions.clicked,
        actionSubject: actionSubjects.card,
        actionSubjectId:
          attributes.diffSettings.fileViewMode === FileViewMode.Tree
            ? actionSubjectIds.fileTreeFile
            : actionSubjectIds.fileListFile,
        source: source.pullRequest,
        objectId: `${attributes.repoUUID}/${attributes.prID}`,
        objectType: source.repository,
        attributes: {
          isMobile: attributes.isMobile,
          expandedWidth: attributes.sidebarWidth,
          diffLineCount: attributes.pullRequestDiffLinesCount,
          numOfFiles: attributes.fileHrefs.length,
          currentSettings: attributes.diffSettings,
          fileAttributes: {
            fileName,
            isConflicted,
            fileDiffStatus,
          },
        },
      });
    },
    [attributes]
  );
};

export const publishPrAnalyticsFileViewFolder = (
  attributes: PullRequestAttribute,
  folderName: string,
  isFolderCollapsed: boolean
) => {
  publishUiEvent({
    action: actions.clicked,
    actionSubject: actionSubjects.card,
    actionSubjectId:
      attributes.diffSettings.fileViewMode === FileViewMode.Tree
        ? actionSubjectIds.fileTreeFolder
        : actionSubjectIds.fileListFolder,
    source: source.pullRequest,
    objectId: `${attributes.repoUUID}/${attributes.prID}`,
    objectType: source.repository,
    attributes: {
      isMobile: attributes.isMobile,
      expandedWidth: attributes.sidebarWidth,
      diffLineCount: attributes.pullRequestDiffLinesCount,
      numOfFiles: attributes.fileHrefs.length,
      currentSettings: attributes.diffSettings,
      folderAttributes: {
        folderName,
        isFolderCollapsed,
      },
    },
  });
};

export const publishPrAnalyticsSidebar = (
  attributes: PullRequestAttribute,
  newWidth: number,
  isCollapsed: boolean
) => {
  const sidebarAttributes = isCollapsed
    ? {
        isCollapsed,
        currentWidth: newWidth,
        expandedWidth: attributes.sidebarWidth,
      }
    : {
        isCollapsed,
        currentWidth: newWidth,
        previousExpandedWidth: attributes.sidebarWidth,
      };

  publishUiEvent({
    action: actions.clicked,
    actionSubject: actionSubjects.sidebar,
    actionSubjectId: actionSubjectIds.sidebarExpander,
    source: source.pullRequest,
    objectId: `${attributes.repoUUID}/${attributes.prID}`,
    objectType: source.repository,
    attributes: {
      isMobile: attributes.isMobile,
      diffLineCount: attributes.pullRequestDiffLinesCount,
      numOfFiles: attributes.fileHrefs.length,
      currentSettings: attributes.diffSettings,
      sidebarAttributes,
    },
  });
};

type TabIndex = { [key: number]: string };
const tabIndex: TabIndex = {
  0: actionSubjectIds.detailsTab,
  1: actionSubjectIds.filesTab,
  2: actionSubjectIds.activitiesTab,
};

export const usePrTabAnalytics = () => {
  const attributes = usePullRequestAttributes();
  return useCallback(
    (index: number) => {
      const tab = tabIndex[index];
      publishUiEvent({
        action: actions.clicked,
        actionSubject: actionSubjects.tab,
        actionSubjectId: tab,
        source: source.pullRequest,
        objectId: `${attributes.repoUUID}/${attributes.prID}`,
        objectType: source.repository,
        attributes: {
          isMobile: attributes.isMobile,
          expandedWidth: attributes.sidebarWidth,
          diffLineCount: attributes.pullRequestDiffLinesCount,
          numOfFiles: attributes.fileHrefs.length,
          currentSettings: attributes.diffSettings,
        },
      });
    },
    [attributes]
  );
};
